/**
 * Per-connector setup sheet (native): the iOS/Android counterpart of
 * {@link ConnectorSetupDialog} (web). Presented as a form sheet from the
 * connectors list.
 *
 * Flow (AI rail: ChatGPT / Claude / Gemini / Grok / Perplexity):
 *   1. Consent: paired Tabbio ↔ provider marks, a "Connect {name}" title, and a
 *      plain-language permissions summary (what it will / won't be able to do),
 *      with Connect / Cancel. This renders synchronously so the sheet is never
 *      blank while the MCP state loads.
 *   2. Finish: after Connect, the real MCP setup: server URL + one-time token to
 *      copy into the assistant, plus the numbered provider steps.
 *
 * Tool rail (Gmail / Slack / …) shows a "coming soon" explainer. Shares the setup
 * spec ({@link connectorSetupSpec}) and the MCP access API with the web dialog.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { Text } from '@/components/common/AppText';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { SvgXml } from 'react-native-svg';
import * as Clipboard from 'expo-clipboard';
import {
  Check,
  CheckCircle2,
  Clock,
  Copy,
  Key,
  Link2,
  Plug,
  Plus,
  X,
} from 'lucide-react-native';
import { colors, spacing } from '@tabbio/tokens';
import { useTheme } from '../../hooks/useTheme';
import { useLocale } from '../../hooks/useLocale';
import { useFont } from '../../providers/FontProvider';
import { useHaptics } from '../../hooks/useHaptics';
import { useToast } from '../../hooks/useToast';
import { NativeButton } from '../common/NativeButton';
import { BRAND_SVG, MONOCHROME_BRAND_SLUGS } from './brand-logos';
import {
  connectorSetupSpec,
  CONSENT_CAN_KEYS,
  CONSENT_CANNOT_KEYS,
  TOKEN_PLACEHOLDER,
} from './connector-setup';
import {
  createMcpToken,
  fetchMcpAccessState,
  revokeMcpToken,
  rotateMcpToken,
  type McpTokenSummary,
} from '../../lib/mcp-access-api';
import {TabbioMark} from "@/components/common/TabbioMark";

const MONO =
  Platform.select({ ios: 'Menlo', android: 'monospace', default: 'monospace' }) ??
  'monospace';
const GREEN = '#1a9c5b';

function activeTokenOf(tokens: McpTokenSummary[]): McpTokenSummary | null {
  return tokens.find((tok) => !tok.revokedAt) ?? null;
}

export interface ConnectorSetupScreenProps {
  slug: string;
  name: string;
  rail: string;
  comingSoon: boolean;
}

export function ConnectorSetupScreen({ slug, name, comingSoon }: ConnectorSetupScreenProps) {
  const { t } = useTranslation('common');
  const { theme } = useTheme();
  const { isRTL } = useLocale();
  const { fonts } = useFont();
  const haptics = useHaptics();
  const toast = useToast();
  const router = useRouter();
  // The root direction mirrors rows once on both platforms.
  const rowDirection = 'row';
  const localizedText = {
    textAlign: 'auto' as const,
    writingDirection: isRTL ? ('rtl' as const) : ('ltr' as const),
  };

  const spec = connectorSetupSpec(slug);
  const brand = BRAND_SVG[slug];
  const isMono = MONOCHROME_BRAND_SLUGS.has(slug);

  const [phase, setPhase] = useState<'consent' | 'setup'>('consent');
  const [serverUrl, setServerUrl] = useState('');
  const [token, setToken] = useState<McpTokenSummary | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [accessError, setAccessError] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [working, setWorking] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  // Load only after consent. Failed reads must never expose empty credentials.
  useEffect(() => {
    if (comingSoon || phase !== 'setup') return;
    let alive = true;
    setLoading(true);
    setAccessError(false);
    void fetchMcpAccessState()
      .then((state) => {
        if (!alive) return;
        setServerUrl(state.serverUrl);
        setToken(activeTokenOf(state.tokens));
      })
      .catch(() => {
        if (alive) setAccessError(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [comingSoon, phase, loadAttempt]);

  const copy = useCallback(
    async (value: string, key: string) => {
      try {
        await Clipboard.setStringAsync(value);
        haptics.trigger('light');
        setCopied(key);
        setTimeout(() => setCopied((c) => (c === key ? null : c)), 1600);
      } catch {
        toast.error(t('connectors.setup.copyFailed'));
      }
    },
    [haptics, toast, t],
  );

  const generate = useCallback(async () => {
    setWorking(true);
    try {
      const result = token
        ? await rotateMcpToken(token.id)
        : await createMcpToken({ includePersonal: true });
      setToken(result.token);
      setSecret(result.secret);
      haptics.trigger('success');
    } catch (error) {
      toast.error(
        t('connectors.setup.tokenFailed'),
        error instanceof Error ? error.message : undefined,
      );
    } finally {
      setWorking(false);
    }
  }, [token, haptics, toast, t]);

  const revoke = useCallback(() => {
    if (!token) return;
    Alert.alert(t('connectors.setup.revokeTitle'), t('connectors.setup.revokeBody'), [
      { text: t('actions.cancel'), style: 'cancel' },
      {
        text: t('connectors.setup.revoke'),
        style: 'destructive',
        onPress: () => {
          void (async () => {
            setWorking(true);
            try {
              await revokeMcpToken(token.id);
              setToken(null);
              setSecret(null);
              toast.success(t('connectors.setup.revoked'));
            } catch (error) {
              toast.error(
                t('connectors.setup.tokenFailed'),
                error instanceof Error ? error.message : undefined,
              );
            } finally {
              setWorking(false);
            }
          })();
        },
      },
    ]);
  }, [token, toast, t]);

  // Match web: reveal setup first, then generate a token explicitly.
  const handleConnect = useCallback(() => {
    haptics.trigger('light');
    setPhase('setup');
  }, [haptics]);

  const steps = t(`connectors.setup.${slug}.steps`, {
    returnObjects: true,
    defaultValue: [],
  }) as string[];
  const codeValue = spec?.code
    ? spec.code(serverUrl || 'https://…/api/mcp', secret ?? TOKEN_PLACEHOLDER)
    : null;

  const openDocs = useCallback(() => {
    if (spec?.docUrl) void Linking.openURL(spec.docUrl);
  }, [spec?.docUrl]);

  const contentTop = Platform.OS === 'ios' ? 72 : spacing[4];

  // ── Provider mark (paired header) ──────────────────────────────────────────
  const providerMark = (
    <View
      style={[
        styles.pairDisc,
        // brand
        //   ? { backgroundColor: theme.surface, borderColor: theme.border, borderWidth: StyleSheet.hairlineWidth }
        //   : { backgroundColor: theme.surfaceSecondary },
      ]}
    >
      {brand ? (
        <SvgXml xml={brand} width={30} height={30} color={isMono ? theme.text : undefined} />
      ) : (
        <Plug size={20} color={theme.textSecondary} strokeWidth={1.8} />
      )}
    </View>
  );

  return (
    // collapsable={false} stops Fabric from flattening this view away. Flattened,
    // its ScrollView + footer children get hoisted into react-native-screens'
    // formSheet safe-area container as extra siblings, which breaks that
    // container's ScrollView-sizing logic (it only handles the scrollview at
    // sibling index 0 or 1) and leaves the sheet's content view with a stale/
    // empty frame: the sheet renders blank.
    <View
      style={[styles.screen, { backgroundColor: theme.background }]}
      collapsable={false}
    >
      <ScrollView
        style={styles.flex}
        contentContainerStyle={[styles.content, { paddingTop: contentTop }]}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        contentInsetAdjustmentBehavior="never"
      >
        {/* Paired brand header */}
        <View style={[styles.pairRow, { flexDirection: rowDirection }]}>
          <View style={[styles.pairDisc]}>
            <TabbioMark color={theme.text} />
          </View>
          <Link2 size={18} color={theme.textTertiary} strokeWidth={2} />
          {providerMark}
        </View>

        <Text style={[styles.title, { color: theme.text, fontFamily: fonts.semibold }]}>
          {t('connectors.connectName', { name })}
        </Text>

        {comingSoon ? (
          <View style={[styles.comingSoonBox, { backgroundColor: theme.surfaceSecondary }]}>
            <Clock size={22} color={theme.textSecondary} strokeWidth={1.8} />
            <Text style={[styles.comingSoonText, { color: theme.textSecondary, fontFamily: fonts.regular }]}>
              {t('connectors.setup.comingSoonBody', { name })}
            </Text>
          </View>
        ) : phase === 'consent' ? (
          // ── Consent ──────────────────────────────────────────────────────────
          <>
            <View style={[styles.permCard, { backgroundColor: theme.surfaceSecondary }]}>
              <Text style={[styles.permLabel, localizedText, { color: theme.textTertiary, fontFamily: fonts.semibold }]}>
                {t('connectors.consent.willBeAbleTo').toUpperCase()}
              </Text>
              {CONSENT_CAN_KEYS.map((key) => (
                <View key={key} style={[styles.permRow, { flexDirection: rowDirection }]}>
                  <Check size={18} color={theme.textSecondary} strokeWidth={2.4} />
                  <Text style={[styles.permText, localizedText, { color: theme.text, fontFamily: fonts.regular }]}>
                    {t(`connectors.consent.${key}`)}
                  </Text>
                </View>
              ))}
              <View style={[styles.permDivider, { backgroundColor: theme.border }]} />
              <Text style={[styles.permLabel, localizedText, { color: theme.textTertiary, fontFamily: fonts.semibold }]}>
                {t('connectors.consent.willNot').toUpperCase()}
              </Text>
              {CONSENT_CANNOT_KEYS.map((key) => (
                <View key={key} style={[styles.permRow, { flexDirection: rowDirection }]}>
                  <X size={18} color={theme.textSecondary} strokeWidth={2.4} />
                  <Text style={[styles.permText, localizedText, { color: theme.text, fontFamily: fonts.regular }]}>
                    {t(`connectors.consent.${key}`)}
                  </Text>
                </View>
              ))}
            </View>
            <Text style={[styles.disconnectNote, { color: theme.textTertiary, fontFamily: fonts.regular }]}>
              {t('connectors.consent.disconnectAnytime')}
            </Text>
          </>
        ) : loading ? (
          <Text style={[styles.finishHint, localizedText, { color: theme.textSecondary }]}>{t('connectors.loading')}</Text>
        ) : accessError ? (
          <View style={styles.sections}>
            <Text accessibilityRole="alert" style={[styles.finishHint, localizedText, { color: theme.textSecondary }]}>{t('connectors.simple.setupFailed')}</Text>
            <NativeButton title={t('actions.retry')} onPress={() => setLoadAttempt((value) => value + 1)} variant="outline" size="md" pill />
          </View>
        ) : (
          // ── Finish setup ───────────────────────────────────────────────────────
          <View style={styles.sections}>
            <View>
              <Text style={[styles.finishTitle, localizedText, { color: theme.text, fontFamily: fonts.semibold }]}>
                {t('connectors.consent.finishTitle', { name })}
              </Text>
              <Text style={[styles.finishHint, localizedText, { color: theme.textSecondary, fontFamily: fonts.regular }]}>
                {t('connectors.consent.finishHint', { name })}
              </Text>
            </View>

            {/* Server URL */}
            <View>
              <SectionLabel>{t('connectors.setup.serverUrl')}</SectionLabel>
              <Pressable
                onPress={() => void copy(serverUrl, 'url')}
                style={({ pressed }) => [
                  styles.fieldRow,
                  { borderColor: theme.border, backgroundColor: theme.surface, opacity: pressed ? 0.85 : 1, flexDirection: rowDirection },
                ]}
              >
                <Text style={[styles.mono, { color: theme.text, flex: 1 }]} numberOfLines={1}>
                  {serverUrl || 'N/A'}
                </Text>
                {copied === 'url' ? (
                  <Check size={15} color={GREEN} strokeWidth={2.4} />
                ) : (
                  <Copy size={15} color={theme.textTertiary} strokeWidth={1.9} />
                )}
              </Pressable>
            </View>

            {/* Token */}
            <View>
              <SectionLabel>{t('connectors.setup.token')}</SectionLabel>
              {secret ? (
                <>
                  <Pressable
                    onPress={() => void copy(secret, 'secret')}
                    style={({ pressed }) => [
                      styles.oneTimeBox,
                      { borderColor: colors.primary, backgroundColor: `${colors.primary}0A`, opacity: pressed ? 0.92 : 1 },
                    ]}
                  >
                    <View style={[styles.oneTimeHead, { flexDirection: rowDirection }]}>
                      <Key size={15} color={colors.primary} strokeWidth={1.9} />
                      <Text style={[styles.oneTimeLabel, localizedText, { color: colors.primary, fontFamily: fonts.semibold, flex: 1 }]}>
                        {t('connectors.setup.token')}
                      </Text>
                      {copied === 'secret' ? (
                        <Check size={15} color={colors.primary} strokeWidth={2.4} />
                      ) : (
                        <Copy size={15} color={colors.primary} strokeWidth={1.9} />
                      )}
                    </View>
                    <Text style={[styles.mono, { color: theme.text }]} selectable>
                      {secret}
                    </Text>
                  </Pressable>
                  <Text style={[styles.hint, localizedText, { color: '#b26a00', fontFamily: fonts.regular }]}>
                    {t('connectors.setup.oneTimeWarning')}
                  </Text>
                </>
              ) : token ? (
                <View style={[styles.fieldRowStack, { borderColor: theme.border, backgroundColor: theme.surface }]}>
                  <View style={[styles.tokenActiveRow, { flexDirection: rowDirection }]}>
                    <CheckCircle2 size={15} color={theme.textSecondary} strokeWidth={1.9} />
                    <Text style={[styles.tokenActiveText, localizedText, { color: theme.textSecondary, fontFamily: fonts.regular, flex: 1 }]}>
                      {t('connectors.setup.tokenActive')}
                    </Text>
                    <Text style={[styles.mono, { color: theme.textTertiary }]}>{token.maskedToken}</Text>
                  </View>
                  <View style={[styles.tokenActions, { flexDirection: rowDirection }]}>
                    <Pressable onPress={() => void generate()} disabled={working} hitSlop={6}>
                      <Text style={[styles.ghostAction, { color: theme.textSecondary, fontFamily: fonts.medium }]}>
                        {t('connectors.setup.regenerate')}
                      </Text>
                    </Pressable>
                    <Pressable onPress={revoke} disabled={working} hitSlop={6}>
                      <Text style={[styles.ghostAction, { color: colors.danger, fontFamily: fonts.medium }]}>
                        {t('connectors.setup.revoke')}
                      </Text>
                    </Pressable>
                  </View>
                </View>
              ) : (
                <NativeButton
                  title={working ? t('connectors.setup.generating') : t('connectors.setup.generateToken')}
                  onPress={() => void generate()}
                  variant="solid"
                  color="contrast"
                  size="md"
                  pill
                  loading={working}
                  disabled={working}
                  iconLeft={<Plus size={17} color={theme.buttonContrastText} strokeWidth={2.2} />}
                />
              )}
            </View>

            {/* Code snippet (command / config) */}
            {codeValue ? (
              <View>
                <SectionLabel>
                  {spec?.codeKind === 'config'
                    ? t('connectors.setup.configLabel')
                    : t('connectors.setup.commandLabel')}
                </SectionLabel>
                <View style={[styles.codeBox, { backgroundColor: theme.surfaceSecondary }]}>
                  <Text style={[styles.code, { color: theme.text }]} selectable>
                    {codeValue}
                  </Text>
                  <CopyChip copied={copied === 'code'} onPress={() => void copy(codeValue, 'code')} />
                </View>
              </View>
            ) : null}

            {/* Steps */}
            {steps.length ? (
              <View>
                <SectionLabel>{t('connectors.setup.stepsLabel')}</SectionLabel>
                <View style={styles.steps}>
                  {steps.map((step, i) => (
                    <View key={i} style={[styles.stepRow, { flexDirection: rowDirection }]}>
                      <View style={[styles.stepNum, { backgroundColor: theme.surfaceSecondary }]}>
                        <Text style={[styles.stepNumText, { color: theme.textSecondary, fontFamily: fonts.semibold }]}>
                          {i + 1}
                        </Text>
                      </View>
                      <Text style={[styles.stepText, localizedText, { color: theme.textSecondary, fontFamily: fonts.regular }]}>
                        {step}
                      </Text>
                    </View>
                  ))}
                </View>
              </View>
            ) : null}
          </View>
        )}
      </ScrollView>

      {/* Footer */}
      <View style={[styles.footer, { borderTopColor: theme.border }]}>
        {comingSoon ? (
          <View style={[styles.footerRow, { flexDirection: rowDirection }]}>
            {spec?.docUrl ? (
              <View style={styles.footerBtn}>
                <NativeButton title={t('connectors.setup.openDocs')} onPress={openDocs} variant="outline" size="md" pill />
              </View>
            ) : null}
            <View style={styles.footerBtn}>
              <NativeButton title={t('connectors.setup.done')} onPress={() => router.back()} variant="solid" color="contrast" size="md" pill />
            </View>
          </View>
        ) : phase === 'consent' ? (
          <>
            <NativeButton
              title={t('connectors.connect')}
              onPress={() => void handleConnect()}
              variant="solid"
              color="contrast"
              size="md"
              pill
            />
            <Pressable onPress={() => router.back()} hitSlop={8} style={styles.cancelBtn}>
              <Text style={[styles.cancelText, { color: theme.textSecondary, fontFamily: fonts.medium }]}>
                {t('actions.cancel')}
              </Text>
            </Pressable>
          </>
        ) : (
          <View style={[styles.footerRow, { flexDirection: rowDirection }]}>
            {spec?.docUrl ? (
              <View style={styles.footerBtn}>
                <NativeButton title={t('connectors.setup.openDocs')} onPress={openDocs} variant="outline" size="md" pill />
              </View>
            ) : null}
            <View style={styles.footerBtn}>
              <NativeButton title={t('connectors.setup.done')} onPress={() => router.back()} variant="solid" color="contrast" size="md" pill />
            </View>
          </View>
        )}
      </View>
    </View>
  );
}

function SectionLabel({ children }: { children: string }) {
  const { isRTL } = useLocale();
  const { theme } = useTheme();
  const { fonts } = useFont();
  return (
    <Text style={[styles.sectionLabel, { color: theme.textTertiary, fontFamily: fonts.semibold, textAlign: 'auto', writingDirection: isRTL ? 'rtl' : 'ltr' }]}>
      {children.toUpperCase()}
    </Text>
  );
}

function CopyChip({ copied, onPress }: { copied: boolean; onPress: () => void }) {
  const { theme } = useTheme();
  const { t } = useTranslation('common');
  const { fonts } = useFont();
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [
        styles.copyChip,
        { borderColor: theme.border, backgroundColor: theme.background, opacity: pressed ? 0.7 : 1 },
      ]}
    >
      {copied ? (
        <Check size={13} color={GREEN} strokeWidth={2.4} />
      ) : (
        <Copy size={13} color={theme.textSecondary} strokeWidth={1.9} />
      )}
      <Text style={[styles.copyChipText, { color: theme.textSecondary, fontFamily: fonts.medium }]}>
        {copied ? t('connectors.setup.copied') : t('connectors.setup.copy')}
      </Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1 },
  flex: { flex: 1 },
  content: { paddingHorizontal: spacing[5], paddingBottom: spacing[6], alignItems: 'stretch' },
  pairRow: { alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: spacing[4] },
  pairDisc: {
    width: 56,
    height: 56,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  tabbioMark: { fontSize: 26, lineHeight: 30 },
  title: { fontSize: 22, fontWeight: '600', textAlign: 'center', marginBottom: spacing[5] },
  // Consent
  permCard: { borderRadius: 18, padding: spacing[4], gap: 10 },
  permLabel: { fontSize: 11, letterSpacing: 0.4 },
  permRow: { alignItems: 'center', gap: 10 },
  permText: { flex: 1, fontSize: 15, lineHeight: 21, writingDirection: 'auto' },
  permDivider: { height: StyleSheet.hairlineWidth, marginVertical: 4 },
  disconnectNote: { fontSize: 13, textAlign: 'center', marginTop: spacing[4] },
  // Finish setup
  sections: { gap: spacing[5] },
  finishTitle: { fontSize: 17, fontWeight: '600', marginBottom: 4 },
  finishHint: { fontSize: 14, lineHeight: 20 },
  sectionLabel: { fontSize: 12, letterSpacing: 0.3, marginBottom: 10 },
  steps: { gap: 12 },
  stepRow: { gap: 12, alignItems: 'flex-start' },
  stepNum: { width: 22, height: 22, borderRadius: 11, alignItems: 'center', justifyContent: 'center', marginTop: 1 },
  stepNumText: { fontSize: 12 },
  stepText: { flex: 1, fontSize: 14, lineHeight: 21, writingDirection: 'auto' },
  codeBox: { borderRadius: 14, padding: 14, gap: 12 },
  code: { fontFamily: MONO, fontSize: 12.5, lineHeight: 19, writingDirection: 'ltr', textAlign: 'auto' },
  copyChip: {
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    height: 28,
    paddingHorizontal: 10,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
  },
  copyChipText: { fontSize: 12 },
  fieldRow: {
    alignItems: 'center',
    gap: 10,
    minHeight: 48,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 14,
    borderWidth: StyleSheet.hairlineWidth,
  },
  fieldRowStack: { borderRadius: 14, borderWidth: StyleSheet.hairlineWidth, padding: 12, gap: 10 },
  mono: { fontFamily: MONO, fontSize: 13, writingDirection: 'ltr', textAlign: 'auto' },
  oneTimeBox: { borderRadius: 14, borderWidth: 1, padding: 12, gap: 8 },
  oneTimeHead: { alignItems: 'center', gap: 8 },
  oneTimeLabel: { fontSize: 13 },
  tokenActiveRow: { alignItems: 'center', gap: 8 },
  tokenActiveText: { fontSize: 13 },
  tokenActions: { alignItems: 'center', gap: 16, justifyContent: 'flex-end' },
  ghostAction: { fontSize: 13 },
  hint: { fontSize: 12, lineHeight: 17, marginTop: 8 },
  comingSoonBox: { alignItems: 'center', gap: 12, paddingVertical: 32, paddingHorizontal: 20, borderRadius: 16 },
  comingSoonText: { fontSize: 14, lineHeight: 20, textAlign: 'center' },
  footer: {
    gap: 8,
    paddingHorizontal: spacing[5],
    paddingTop: spacing[3],
    paddingBottom: spacing[5],
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  footerRow: { alignItems: 'center', gap: 10 },
  footerBtn: { flex: 1 },
  cancelBtn: { alignItems: 'center', justifyContent: 'center', paddingVertical: 12 },
  cancelText: { fontSize: 15 },
});
